import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ChevronIcon from './ChevronIcon';
import type { Workstream } from '../types/workstream';

interface ShortName {
  name: string;
  workstreamID: string;
}

interface TaskSettingsProps {
  workstream: Workstream;
  onWorkstreamChanged?: () => void;
}

export default function TaskSettings({ workstream, onWorkstreamChanged }: TaskSettingsProps) {
  const [expanded, setExpanded] = useState(false);
  const [shortNames, setShortNames] = useState<ShortName[]>([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(workstream.defaultShortName);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<ShortName | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const wsUrl = `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}`;

  useEffect(() => {
    setDraft(workstream.defaultShortName);
  }, [workstream.defaultShortName]);

  // Fetch short names when expanded.
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    fetch(`${wsUrl}/shortnames`)
      .then(async (resp) => {
        if (cancelled || !resp.ok) return;
        const data: ShortName[] = await resp.json();
        if (!cancelled) setShortNames(data ?? []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [expanded, wsUrl, workstream.version]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const alternateNames = shortNames.filter(
    (sn) => sn.name.toLowerCase() !== workstream.defaultShortName.toLowerCase(),
  );

  const save = useCallback(async () => {
    const trimmed = draft.trim().toUpperCase();
    if (!trimmed || trimmed.toLowerCase() === workstream.defaultShortName.toLowerCase()) {
      setEditing(false);
      setDraft(workstream.defaultShortName);
      return;
    }
    setSaving(true);
    setError(null);
    setConflict(null);
    try {
      // Check if the name is already taken by another workstream.
      const lookupResp = await fetch(`/api/shortnames/${encodeURIComponent(trimmed)}`);
      if (lookupResp.ok) {
        const existing: ShortName | null = await lookupResp.json();
        if (existing && existing.workstreamID !== workstream.workstreamID) {
          // Name belongs to another workstream — show conflict dialog.
          setConflict(existing);
          setSaving(false);
          return;
        }
      }

      // Add the short name if it doesn't already exist on this workstream.
      // The local shortNames list may be stale, so treat a 409 from the
      // server as "already exists" and proceed to set it as default.
      const existsOnThis = shortNames.some((sn) => sn.name.toLowerCase() === trimmed.toLowerCase());
      let added = false;
      if (!existsOnThis) {
        const addResp = await fetch(`${wsUrl}/shortnames/${encodeURIComponent(trimmed)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: workstream.version }),
        });
        if (addResp.ok) {
          added = true;
        } else if (addResp.status !== 409) {
          const text = await addResp.text();
          setError(text || 'Failed to add short name');
          setSaving(false);
          return;
        }
      }

      // Update the default short name.
      const updateResp = await fetch(wsUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Version: workstream.version + (added ? 1 : 0), DefaultShortName: trimmed }),
      });
      if (!updateResp.ok) {
        const text = await updateResp.text();
        setError(text || 'Failed to update default short name');
        if (added) {
          // The add succeeded (version bumped server-side) but the PATCH
          // failed.  Refresh so the parent has the current version and a
          // retry won't 409.
          onWorkstreamChanged?.();
        }
      } else {
        onWorkstreamChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }, [draft, workstream, shortNames, wsUrl, onWorkstreamChanged]);

  const handleReassign = useCallback(async () => {
    if (!conflict) return;
    setSaving(true);
    setError(null);
    try {
      // Fetch the source workstream to get its version.
      const srcResp = await fetch(`/api/workstreams/${encodeURIComponent(conflict.workstreamID)}`);
      if (!srcResp.ok) {
        setError('Failed to fetch source workstream');
        setSaving(false);
        return;
      }
      const srcWs: Workstream = await srcResp.json();

      const moveResp = await fetch(`/api/shortnames/${encodeURIComponent(conflict.name)}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          SourceWorkstreamID: conflict.workstreamID,
          DestinationWorkstreamID: workstream.workstreamID,
          SourceVersion: srcWs.version,
          DestinationVersion: workstream.version,
        }),
      });
      if (!moveResp.ok) {
        const text = await moveResp.text();
        setError(text || 'Failed to reassign prefix');
      } else {
        onWorkstreamChanged?.();
      }
    } catch (err: any) {
      setError(err.message || 'Operation failed');
    } finally {
      setSaving(false);
      setConflict(null);
      setEditing(false);
    }
  }, [conflict, workstream, wsUrl, onWorkstreamChanged]);

  const cancel = useCallback(() => {
    setDraft(workstream.defaultShortName);
    setEditing(false);
    setConflict(null);
  }, [workstream.defaultShortName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (!editing) return;
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      if (!conflict) save();
    },
    [editing, save, conflict],
  );

  const [deleting, setDeleting] = useState(false);

  const handleDeleteShortName = useCallback(async (name: string) => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const resp = await fetch(`${wsUrl}/shortnames/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Version: workstream.version }),
      });
      if (resp.ok || resp.status === 204) {
        onWorkstreamChanged?.();
      } else {
        const text = await resp.text();
        setError(text || 'Failed to delete short name');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete short name');
    } finally {
      setDeleting(false);
    }
  }, [deleting, wsUrl, workstream.version, onWorkstreamChanged]);

  return (
    <div style={{ marginTop: '16px' }}>
      {/* Collapsible header */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((ex) => !ex);
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '14px',
          userSelect: 'none',
        }}
      >
        <span style={{
          display: 'inline-block',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>
          <ChevronIcon size={16} />
        </span>
        Task Settings
      </div>

      {expanded && (
        <div style={{ marginTop: '12px', paddingLeft: '20px' }}>
          {/* Default Task Prefix */}
          <div
            ref={containerRef}
            onBlur={handleBlur}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}
          >
            <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '120px' }}>
              Default Task Prefix
            </label>
            {editing ? (
              <>
                <input
                  ref={inputRef}
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={saving}
                  style={{
                    fontSize: '14px',
                    padding: '4px 8px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    outline: 'none',
                    width: '300px',
                  }}
                />
                <button type="button" className="btn-small" disabled={saving} onClick={save}>Save</button>
                <button type="button" className="btn-small btn-secondary" disabled={saving} onClick={cancel}>Cancel</button>
              </>
            ) : (
              <span
                role="button"
                tabIndex={0}
                onClick={() => setEditing(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setEditing(true);
                  }
                }}
                style={{
                  fontSize: '14px',
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  minWidth: '300px',
                  display: 'inline-block',
                }}
              >
                {workstream.defaultShortName?.toUpperCase() || '(none)'}
              </span>
            )}
          </div>

          {/* Alternate Prefixes */}
          {alternateNames.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '120px' }}>
                Alternate Prefixes
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {alternateNames.map((sn) => (
                  <span
                    key={sn.name}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 10px',
                      borderRadius: '6px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {sn.name.toUpperCase()}
                    <button
                      type="button"
                      disabled={deleting}
                      onClick={() => handleDeleteShortName(sn.name)}
                      title={`Remove ${sn.name.toUpperCase()}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0 2px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#656d76',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              padding: '8px 12px',
              background: '#ffebe9',
              border: '1px solid #cf222e',
              borderRadius: '6px',
              color: '#cf222e',
              fontSize: '13px',
              marginBottom: '8px',
            }}>
              {error}
            </div>
          )}

          {/* Conflict dialog */}
          {conflict && (
            <div style={{
              padding: '12px 16px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              fontSize: '14px',
              marginBottom: '8px',
            }}>
              <p style={{ margin: '0 0 8px' }}>
                The prefix name &quot;{conflict.name}&quot; is already in use by{' '}
                <Link
                  to={`/workstreams/${encodeURIComponent(conflict.workstreamID)}`}
                  style={{ color: '#0969da' }}
                >
                  another workstream
                </Link>
                .
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="button" className="btn-small btn-danger" disabled={saving} onClick={handleReassign}>
                  Reassign Prefix
                </button>
                <button type="button" className="btn-small btn-secondary" disabled={saving} onClick={cancel}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
